import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n';
import { homePathFor, isHomePath } from '../i18n/localePath';
import { getCounterpartPath } from '../legal/routes';
import { getContentTestingCounterpart, getContentTestingFromPath } from '../content-testing/routes';
import Chatbot from './Chatbot';
import { scrollToSection } from '../utils/scrollToSection';

const MOBILE_BREAKPOINT = '(min-width: 1001px)';

/**
 * Header: sticky, white, with the original wordmark, four section links, a
 * "Let's talk" link, the EN/DE switch and a hamburger at 1000px and below.
 * Section links scroll on the homepage and navigate to `/#section` elsewhere.
 */
const Header: React.FC = () => {
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  const isHome = isHomePath(location.pathname);
  const homePath = homePathFor(language);

  const sections = [
    { id: 'contextfriend', label: t.header.nav.contextfriend },
    { id: 'work', label: t.header.nav.work },
    { id: 'about', label: t.header.nav.about },
    { id: 'faq', label: t.header.nav.faq }
  ];

  useEffect(() => {
    if (!menuOpen) return;

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    const handleClick = (event: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    const media = window.matchMedia(MOBILE_BREAKPOINT);
    const handleMedia = (event: MediaQueryListEvent) => {
      if (event.matches) setMenuOpen(false);
    };

    document.addEventListener('keydown', handleKey);
    document.addEventListener('click', handleClick);
    media.addEventListener?.('change', handleMedia);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('click', handleClick);
      media.removeEventListener?.('change', handleMedia);
    };
  }, [menuOpen]);

  const handleSectionClick = (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    setMenuOpen(false);
    if (isHome && scrollToSection(id)) {
      event.preventDefault();
    }
  };

  const handleLogoClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    setMenuOpen(false);
    if (isHome) {
      event.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const toggleLanguage = () => {
    const next = language === 'en' ? 'de' : 'en';
    const counterpart =
      getCounterpartPath(location.pathname) || getContentTestingCounterpart(location.pathname);
    setLanguage(next);
    if (counterpart) {
      navigate(counterpart);
    } else if (isHome) {
      navigate(next === 'de' ? '/de' : '/');
    } else {
      navigate(homePathFor(next));
    }
    setMenuOpen(false);
  };

  return (
    <header className="lp-header" role="banner" ref={headerRef}>
      <div className="lp-wrap lp-header-inner">
        <Link className="lp-brand" to={homePath} aria-label={t.header.home} onClick={handleLogoClick}>
          <img src="/logo.png" alt="TamerinTECH - Machine Learns, You Grow" width={216} height={52} />
        </Link>

        <nav
          className={`lp-nav${menuOpen ? ' is-open' : ''}`}
          id="site-navigation"
          aria-label={t.header.navLabel}
        >
          {sections.map((section) => (
            <Link
              key={section.id}
              to={`${homePath}#${section.id}`}
              onClick={(event) => handleSectionClick(event, section.id)}
            >
              {section.label}
            </Link>
          ))}
          <Link
            className="lp-nav-contact"
            to={`${homePath}#contact`}
            onClick={(event) => handleSectionClick(event, 'contact')}
          >
            <span>{t.header.nav.contact}</span>
            <span aria-hidden="true">↗</span>
          </Link>
        </nav>

        <div className="lp-header-tools">
          <button
            type="button"
            className="lp-language"
            onClick={toggleLanguage}
            aria-label={t.header.switchLanguage}
          >
            {language.toUpperCase()} <span aria-hidden="true">⌄</span>
          </button>
          <Chatbot
            navigationOpen={menuOpen}
            onOpen={() => setMenuOpen(false)}
            key={getContentTestingFromPath(location.pathname)
              ? `content-testing-${getContentTestingFromPath(location.pathname)?.currency}`
              : 'website'}
          />
          <button
            type="button"
            className="lp-menu"
            aria-controls="site-navigation"
            aria-expanded={menuOpen}
            aria-label={menuOpen ? t.header.menuClose : t.header.menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span />
            <span />
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
